import { InitData, handoffUrl } from '../core'
import { t, formatMoney, plural } from '../core/i18n'

/** Et klippekort eller abonnement der kan dække holdet (svar fra GET /api/widget/class-plans). */
export interface CoveringPlan {
  id: string
  name: string
  /** Kun til visning. Købet regner serveren selv (/api/widget/memberships/checkout). */
  price: number
  membershipCategory: 'klippekort' | 'subscription'
  totalClasses: number | null
  validityDays: number | null
  activatesFrom: string | null
}

interface Props {
  plans: CoveringPlan[]
  initData: InitData
  /** Studiets adresse, til abonnementets handoff-link. */
  tenantBaseUrl: string
  /** Kunden trykker «Køb» på et klippekort: kalderen åbner det eksisterende købsvindue. */
  onBuy: (plan: CoveringPlan) => void
  /** Hold der kun kan bookes med klippekort: overskrift uden «Eller», og aldrig abonnementer. */
  kunKlippekort?: boolean
}

/** Gyldigt svar: en liste af planer med de felter vinduet bruger. Alt andet ignoreres. */
export function parseCoveringPlans(data: unknown): CoveringPlan[] {
  if (!Array.isArray(data)) return []
  return data.filter((p): p is CoveringPlan =>
    !!p && typeof p.id === 'string' && typeof p.name === 'string' && typeof p.price === 'number'
    && (p.membershipCategory === 'klippekort' || p.membershipCategory === 'subscription'))
}

/**
 * «Eller køb et klippekort» (OPG-0185): under enkeltbilletten i «dette hold skal betales»-vinduet.
 * Klippekort købes inline via det eksisterende købsvindue; abonnementer er et handoff til
 * studiets prisside (aldrig inline). Der bookes ikke automatisk efter købet: noten siger, at
 * kunden åbner holdet igen og trykker Book, så bruges det nye kort.
 */
export function ClassPlanChoice({ plans: alle, initData, tenantBaseUrl, onBuy, kunKlippekort = false }: Props) {
  const s = t(initData.locale)
  // Serveren filtrerer, men widgetten viser heller aldrig et abonnement på et klippekort-only hold.
  const plans = kunKlippekort ? alle.filter(p => p.membershipCategory === 'klippekort') : alle
  if (plans.length === 0) return null
  return (
    <div class="hsw-cp" data-testid="class-plan-choice">
      <div class="hsw-cp-heading">{kunKlippekort ? s.classPlansHeadingOnly : s.classPlansHeading}</div>
      <ul class="hsw-cp-list">
        {plans.map(plan => (
          <li key={plan.id} class="hsw-cp-item">
            <div class="hsw-cp-info">
              <div class="hsw-cp-name">{plan.name}</div>
              <div class="hsw-cp-meta">
                {plan.membershipCategory === 'klippekort'
                  ? [
                      plan.totalClasses != null ? plural(s.classPlansClips, plan.totalClasses, initData.locale) : null,
                      plan.validityDays != null && plan.validityDays > 0 ? plural(s.classPlansValidDays, plan.validityDays, initData.locale) : null,
                    ].filter(Boolean).join(' · ')
                  : s.classPlansSubscription}
              </div>
            </div>
            <div class="hsw-cp-side">
              <div class="hsw-cp-price">{formatMoney(plan.price, initData.locale, initData.currency)}</div>
              {plan.membershipCategory === 'klippekort' ? (
                <button type="button" class="hsw-cp-buy" onClick={() => onBuy(plan)}>{s.classPlansBuy}</button>
              ) : (
                <a class="hsw-cp-link" href={handoffUrl(tenantBaseUrl, `/pricing?plan=${plan.id}&autoStart=1`)} target="_blank" rel="noopener">
                  {s.classPlansViewSubscription}
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p class="hsw-cp-note">{s.classPlansAfterNote}</p>
    </div>
  )
}

export const classPlanChoiceStyles = `
  .hsw-cp { width: 100%; margin: 1rem 0 0.25rem; text-align: left; }
  .hsw-cp-heading { font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--yoga-text-muted, #737373); margin-bottom: 0.5rem; }
  .hsw-cp-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
  .hsw-cp-item { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.7rem 0.85rem; border: 1px solid var(--yoga-card-border, #e5e7eb); border-radius: var(--yoga-radius, 6px); background: var(--yoga-card-bg, #fff); }
  .hsw-cp-info { min-width: 0; }
  .hsw-cp-name { font-weight: 600; font-size: 0.95rem; color: var(--yoga-text, #0a0a0a); overflow-wrap: anywhere; }
  .hsw-cp-meta { font-size: 0.8rem; color: var(--yoga-text-muted, #737373); margin-top: 0.15rem; }
  .hsw-cp-side { display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0; }
  .hsw-cp-price { font-weight: 700; font-size: 0.95rem; white-space: nowrap; }
  .hsw-cp-buy { background: var(--yoga-primary, #FF6B35); color: #fff; border: 0; border-radius: var(--yoga-radius, 6px); padding: 0.5rem 1rem; font-size: 0.85rem; font-weight: 600; cursor: pointer; font-family: inherit; }
  .hsw-cp-buy:hover { filter: brightness(0.94); }
  .hsw-cp-link { font-size: 0.85rem; font-weight: 600; color: var(--yoga-primary, #FF6B35); text-decoration: underline; white-space: nowrap; }
  .hsw-cp-note { font-size: 0.8rem; line-height: 1.45; color: var(--yoga-text-muted, #737373); margin: 0.65rem 0 0; }
  @media (max-width: 480px) {
    .hsw-cp-item { flex-direction: column; align-items: stretch; }
    .hsw-cp-side { justify-content: space-between; }
  }
`
